import {matchPath} from "react-router";
import {Faction, User} from "../types/general.types.ts";

/**
 * Product analytics through a self-hosted Umami instance.
 * Only active when VITE_UMAMI_URL and VITE_UMAMI_WEBSITE_ID are set (prod / staging).
 */

type EventData = Record<string, string | number | boolean | null>;

type AfImportSource = 'xml' | 'log';
type AfRejectReason = 'layout-mismatch' | 'other-field' | 'too-old' | 'parse-error' | 'no-events';

// Tools using SharedSave (cargo, fitout, vision, map) share the same save/load events
type SavedItemEvents = {
    [K in SaveItemType as `${K}:save`]: { isNew: boolean, isPublic?: boolean }
} & {
    [K in SaveItemType as `${K}:load` | `${K}:delete` | `${K}:share`]: undefined
};

// Every custom event and the shape of its data, event names follow `<tool>:<action>`
export type AnalyticsEvents = SavedItemEvents & {
    // Asteroid fields
    'af:field-create': undefined,
    'af:field-delete': undefined,
    'af:field-rename': undefined,
    'af:field-reset': undefined,
    'af:max-deposits-set': undefined,
    'af:xml-import': { initial: boolean, events: number, deposits: number },
    'af:log-import': { events: number, deposits: number },
    'af:import-rejected': { source: AfImportSource, reason: AfRejectReason },

    // Vision
    'vision:af-import': { format: 'layout' | 'fieldString' | 'unknown' },
    'vision:mode-change': { mode: string },
    'vision:location-change': { location: string },
    'vision:optimize': { sensors: number, outcome: 'optimal' | 'bound' | 'uncoverable' | 'error', added: number, ms: number },
    'vision:optimize-cancel': undefined,

    // Map
    'map:mode-change': { mode: string },
    'map:export-png': { resolution: number },
    'map:marker-add': undefined,

    // Cargo / Fitout
    'cargo:entity-add': { containerType: string },
    'cargo:paste': { items: number },
    'fitout:container-select': { kind: string },
    'fitout:entity-add': undefined,

    // Hyperlanes
    'hl:route-calculate': { fromSystem: boolean, toSystem: boolean, excludes: number, avoidPvp: boolean, named: boolean },
    'hl:route-calculate-failed': { status: number },
    'hl:route-refresh': { from: 'list' | 'details' },
    'hl:route-delete': undefined,
    'hl:route-rename': undefined,
    'hl:route-alt-select': { index: number },
    'hl:route-flip': { flipped: boolean },
    'hl:preset-create': undefined,
    'hl:preset-delete': undefined,
    'hl:preset-default': undefined,
    'hl:preset-select': { from: 'list' | 'details' },
    'hl:ship-select': undefined,
};

type UmamiPayload = {
    url?: string,
    referrer?: string,
    id?: string,
    [key: string]: unknown,
};

declare global {
    interface Window {
        umami?: {
            track: (name?: string, data?: EventData) => Promise<void>,
            identify: (id: string, data?: EventData) => Promise<void>,
        };
        forgeUmamiBeforeSend?: (type: string, payload: UmamiPayload) => UmamiPayload | false;
    }
}

const UMAMI_URL = import.meta.env.VITE_UMAMI_URL as string | undefined;
const WEBSITE_ID = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;

// Route patterns with ids, params listed in KEPT_PARAMS are meaningful and stay in the url
const ROUTE_PATTERNS = [
    '/bh/contract/:id',
    '/bh/taverns/:view/:network',
    '/hl/routes/:id',
    '/vision/:id',
    '/map/:id',
    '/cargo/cargo/:id',
    '/cargo/fitout/:id',
    '/asteroid-fields/:id',
    '/modules/production/:moduleId/*',
    '/vendors/:vendorId',
    '/wares/:type/:wareKey',
];
const KEPT_PARAMS = ['view', 'type', '*'];

let loaded = false;
let identity: { id: string, traits: EventData } | null = null;

export function normalizePath(pathname: string): string {
    for (const pattern of ROUTE_PATTERNS) {
        const match = matchPath(pattern, pathname);
        if (match) {
            return pattern.replace(/:(\w+)|\*/g, (token, name) => {
                const param = name ?? '*';
                return KEPT_PARAMS.includes(param) ? match.params[param] ?? '' : token;
            });
        }
    }
    return pathname;
}

function normalizeUrl(raw?: string): string | undefined {
    if (!raw) return raw;
    try {
        const url = new URL(raw);
        if (url.origin !== window.location.origin) return raw;
        url.pathname = normalizePath(url.pathname);
        return url.toString();
    } catch {
        return raw;
    }
}

function beforeSend(_type: string, payload: UmamiPayload): UmamiPayload {
    return {
        ...payload,
        url: normalizeUrl(payload.url),
        referrer: normalizeUrl(payload.referrer),
        // Umami keeps the last identified id, ours is cleared on logout
        id: identity?.id,
    };
}

function applyIdentity() {
    if (identity && window.umami) {
        window.umami.identify(identity.id, identity.traits);
    }
}

/**
 * Loads the tracker once the user is known, admins are never tracked.
 * Can be called again when the user changes (login / logout) to update the identity.
 */
export function startAnalytics(user: User | undefined, factions: Faction[] | undefined, analyticsId: string | null) {
    if (!UMAMI_URL || !WEBSITE_ID || user?.isAdmin) return;

    const previousId = identity?.id;
    identity = user && analyticsId ? {
        id: analyticsId,
        traits: {
            tier: user.subLevel,
            hasFaction: (factions?.length ?? 0) > 0,
        },
    } : null;

    if (loaded) {
        if (identity && identity.id !== previousId) applyIdentity();
        return;
    }
    loaded = true;

    window.forgeUmamiBeforeSend = beforeSend;

    const script = document.createElement('script');
    script.defer = true;
    script.src = UMAMI_URL.replace(/\/$/, '') + '/script.js';
    script.dataset.websiteId = WEBSITE_ID;
    script.dataset.excludeSearch = 'true';
    script.dataset.excludeHash = 'true';
    script.dataset.beforeSend = 'forgeUmamiBeforeSend';
    script.onload = applyIdentity;
    document.head.appendChild(script);
}

export function track<E extends keyof AnalyticsEvents>(
    event: E,
    ...data: AnalyticsEvents[E] extends undefined ? [] : [AnalyticsEvents[E]]
) {
    window.umami?.track(event, data[0] as EventData | undefined);
}
